import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { decideTimesheetEntry, fetchTimesheetEntry } from '../api';
import { StatusChip } from '../shared/StatusChip';
import { STATUSES } from '../shared/constants';
import { validateApproveRefuse } from '../shared/validation';
import {
  formatBandHours,
  formatCategoryLabel,
  formatWorkDateDisplay,
} from '../shared/format';
import { useTimesheetPageStyles } from '../shared/pageStyles';

const sectionDisplayValue = section => {
  const segments =
    section.calc && Array.isArray(section.calc.segments)
      ? section.calc.segments
      : [];
  if (
    (section.category === 'overtime' ||
      section.category === 'on_call_onsite') &&
    segments.length
  ) {
    return formatBandHours(segments);
  }
  if (section.category === 'on_call_travel' && section.startTime && section.endTime) {
    const hours =
      section.calc && section.calc.payableTravelHours != null
        ? ` · ${section.calc.payableTravelHours} hours`
        : '';
    return `${section.startTime} – ${section.endTime}${hours} at 1.5×`;
  }
  if (section.startTime && section.endTime) {
    return `${section.startTime} – ${section.endTime}`;
  }
  if (section.onCallAllowance != null) return `£${section.onCallAllowance}`;
  if (section.category === 'collecting_kit' || section.kitCollectionTime) {
    return section.kitCollectionTime
      ? `${section.kitCollectionTime} · 1 hour`
      : '1 hour';
  }
  return 'Included';
};

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    borderRadius: 12,
    border: '1px solid #e1dede',
    boxShadow: 'none',
    maxWidth: 720,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.25),
    gap: theme.spacing(2),
  },
  label: { color: '#757575' },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

const Row = ({ label, value }) => {
  const classes = useStyles();
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography align="right">{value}</Typography>
    </Box>
  );
};

const TimesheetReview = ({
  users,
  basePath,
  appname,
  currentUser,
  history,
  match,
}) => {
  const classes = useStyles();
  const pageClasses = useTimesheetPageStyles();
  const entryId = match && match.params && match.params.entryId;
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const busyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!appname || !entryId) {
      setLoading(false);
      setLoadError('Missing entry');
      return undefined;
    }

    setLoading(true);
    setLoadError(null);
    fetchTimesheetEntry(entryId, appname)
      .then(next => {
        if (cancelled) return;
        setEntry(next);
        setComment(next.fmComment || '');
      })
      .catch(err => {
        if (cancelled) return;
        console.error(err);
        setEntry(null);
        setLoadError(err.message || 'Entry not found');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appname, entryId]);

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Box>
        <Typography>
          {loadError || 'Entry not found or not in your assignment scope.'}
        </Typography>
        <Button color="primary" onClick={() => history.push(basePath)}>
          Back to logs
        </Button>
      </Box>
    );
  }

  const engineer = users.find(u => u.id === entry.engineerId);
  const locked = entry.status === STATUSES.APPROVED;
  const isDraft = entry.status === STATUSES.DRAFT;
  const commentReadOnly = locked || isDraft;

  const decide = async status => {
    if (busyRef.current) return;
    const result = validateApproveRefuse({ status, fmComment: comment });
    if (!result.valid) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    busyRef.current = true;
    setSaving(true);
    setSavingAction(
      status === STATUSES.APPROVED ? 'approve' : 'refuse',
    );
    try {
      await decideTimesheetEntry(entry.id, {
        appname,
        status,
        fmComment: comment,
        fmId: currentUser && currentUser.id,
      });
      history.push(basePath);
    } catch (err) {
      setErrors(err.errors || [err.message]);
      busyRef.current = false;
      setSaving(false);
      setSavingAction(null);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography className={pageClasses.pageTitle}>
          Review Entry
        </Typography>
        <StatusChip status={entry.status} />
      </Box>

      <Paper className={classes.paper}>
        <Row
          label="Engineer"
          value={engineer ? engineer.name : entry.engineerId}
        />
        <Row label="Department" value={engineer ? engineer.department : '—'} />
        <Row label="Date" value={formatWorkDateDisplay(entry.workDate)} />
        <Row label="Category" value={formatCategoryLabel(entry)} />
        {Array.isArray(entry.sections) && entry.sections.length
          ? entry.sections
              .filter(
                section =>
                  section.category !== 'standard_hours' &&
                  !section.workedStandardHours,
              )
              .map(section => (
                <Row
                  key={section.key || section.label}
                  label={section.label || section.category}
                  value={sectionDisplayValue(section)}
                />
              ))
          : null}
        <Row
          label="Standard hours"
          value={
            entry.workedStandardHours
              ? `Yes (${
                  [8, 8.5, 6].indexOf(Number(entry.standardHours)) !== -1
                    ? Number(entry.standardHours)
                    : 8
                }h)`
              : 'No'
          }
        />
        <Row
          label="Time"
          value={
            entry.category !== 'day_sheet' && entry.workedStandardHours
              ? null
              : entry.startTime && entry.endTime
                ? `${entry.startTime} – ${entry.endTime}`
                : null
          }
        />
        <Row label="Customer" value={entry.customerName} />
        <Row label="Job description" value={entry.jobDescription} />
        <Row
          label="On-call allowance"
          value={
            entry.onCallAllowance != null ? `£${entry.onCallAllowance}` : null
          }
        />
        <Row
          label="Collecting kit"
          value={
            entry.collectingKit
              ? `Yes${
                  entry.kitCollectionTime ? ` @ ${entry.kitCollectionTime}` : ''
                } · 1 hour`
              : null
          }
        />
        <Row
          label="Multiplier"
          value={
            entry.category !== 'day_sheet' && entry.workedStandardHours
              ? null
              : (entry.category === 'overtime' ||
                  entry.category === 'on_call_onsite') &&
                entry.calc &&
                Array.isArray(entry.calc.segments) &&
                entry.calc.segments.length
                ? formatBandHours(entry.calc.segments)
                : entry.multiplierLabel
          }
        />
        <Row label="Engineer comment" value={entry.engineerComment} />

        <Divider style={{ margin: '16px 0' }} />

        {errors.length > 0 ? (
          <Box mb={2}>
            {errors.map(err => (
              <Typography key={err} color="error" variant="body2">
                {err}
              </Typography>
            ))}
          </Box>
        ) : null}

        <TextField
          label="FM comment"
          value={comment}
          onChange={e => {
            setComment(e.target.value);
            setErrors([]);
          }}
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          disabled={commentReadOnly || saving}
          helperText={
            locked
              ? 'Approved entries are locked. Engineer can only edit after refusal.'
              : isDraft
                ? 'Draft entries are not ready for review. Approve / Refuse is unavailable.'
                : 'Required when refusing an entry.'
          }
        />

        <Box className={classes.actions}>
          <Button
            variant="outlined"
            onClick={() => history.push(basePath)}
            disabled={saving}
          >
            Back
          </Button>
          {!commentReadOnly ? (
            <>
              <Button
                variant="contained"
                style={{ background: '#2e7d32', color: '#fff' }}
                onClick={() => decide(STATUSES.APPROVED)}
                disabled={saving}
              >
                {savingAction === 'approve' ? 'Approving…' : 'Approve'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => decide(STATUSES.REFUSED)}
                disabled={saving}
              >
                {savingAction === 'refuse' ? 'Refusing…' : 'Refuse'}
              </Button>
            </>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default TimesheetReview;
